import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Baby, Users, Globe2, ToyBrick } from "lucide-react";

const tempId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Quick-add presets the vendor sees as one-click chips. Common European-rental staples.
const PRESETS = [
  { name: "Baby Seat (1–3 years)", price: 5, pricing: "per_day", description: "ISOFIX child seat for toddlers", icon: Baby },
  { name: "Booster Seat (4–10 years)", price: 4, pricing: "per_day", description: "Booster cushion for older children", icon: ToyBrick },
  { name: "Cross-border fee", price: 30, pricing: "one_time", description: "Permits the vehicle to cross national borders", icon: Globe2 },
  { name: "Second driver", price: 6, pricing: "per_day", description: "Add one named additional driver", icon: Users },
];

export default function AddOnsEditor({ value = [], onChange }) {
  const add = (preset = null) => {
    const base = preset
      ? { name: preset.name, price: preset.price, pricing: preset.pricing, description: preset.description }
      : { name: "", price: 0, pricing: "per_day", description: "" };
    onChange([...value, { id: tempId(), ...base }]);
  };

  const update = (idx, patch) => {
    onChange(value.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  // Names already added — avoid offering the same preset twice.
  const taken = new Set(value.map((a) => (a.name || "").trim().toLowerCase()));

  return (
    <div className="rounded-2xl border border-border p-4 space-y-3 bg-neutral-50/40" data-testid="add-ons-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Additional services</Label>
          <p className="text-xs text-neutral-500 mt-1">Optional extras the customer can attach at checkout — child seats, second driver, cross-border permission, etc.</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => add()} className="rounded-full flex-shrink-0" data-testid="add-on-create-blank">
          <Plus className="h-3.5 w-3.5 mr-1" /> Custom
        </Button>
      </div>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => {
          const Icon = p.icon;
          const disabled = taken.has(p.name.toLowerCase());
          return (
            <button
              key={p.name}
              type="button"
              disabled={disabled}
              onClick={() => add(p)}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition ${
                disabled
                  ? "border-border bg-neutral-100 text-neutral-400 cursor-not-allowed"
                  : "border-border bg-white text-neutral-700 hover:border-blue-400 hover:bg-blue-50"
              }`}
              data-testid={`add-on-preset-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              <Icon className="h-3 w-3" />
              {p.name}
            </button>
          );
        })}
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-neutral-400 italic pt-2">No add-ons yet. Click a preset above or "Custom" for something specific.</p>
      ) : (
        <div className="space-y-2 pt-2">
          {value.map((a, i) => (
            <div key={a.id || i} className="rounded-xl bg-white border border-border p-3 space-y-2" data-testid={`add-on-row-${i}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_120px_140px] gap-2">
                  <Input
                    value={a.name || ""}
                    onChange={(e) => update(i, { name: e.target.value })}
                    placeholder="Add-on name"
                    className="rounded-lg"
                    data-testid={`add-on-name-${i}`}
                  />
                  <Input
                    type="number" min="0" step="0.5"
                    value={a.price ?? 0}
                    onChange={(e) => update(i, { price: e.target.value })}
                    placeholder="Price (€)"
                    className="rounded-lg"
                    data-testid={`add-on-price-${i}`}
                  />
                  <Select value={a.pricing || "per_day"} onValueChange={(v) => update(i, { pricing: v })}>
                    <SelectTrigger className="rounded-lg" data-testid={`add-on-pricing-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_day">Per day</SelectItem>
                      <SelectItem value="one_time">One-time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button" variant="ghost" size="icon"
                  onClick={() => remove(i)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  data-testid={`add-on-remove-${i}`}
                  aria-label="Remove add-on"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                rows={1}
                value={a.description || ""}
                onChange={(e) => update(i, { description: e.target.value })}
                placeholder="Short description shown to the customer"
                className="rounded-lg text-xs resize-none"
                data-testid={`add-on-description-${i}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
